import React, { useEffect, useState, createRef, forwardRef } from "react";
import { useTheme, Checkbox, List } from "react-native-paper";
import { useSelector, useDispatch } from "react-redux";
import { TextInputMask } from "react-native-masked-text";
import { View, StyleSheet, Image, Platform, Linking, Dimensions } from "react-native";
import {
  Screen,
  Button,
  Text,
  TextInput,
  InfoBox,
  InputAccessoryNavigation,
} from "../../components";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "../../components/layout/stack";
import { useNavigation } from "@react-navigation/native";
import { ThemeType } from "../../theme/theme";
import { useForm } from "react-hook-form";
import { translate } from "../../i18n";
import { USStateDropdown, MonthYearDropdown } from "../../components/dropdown";
import { getErrorMessageId, ERROR_CODES } from "../../utils/errorCodes";
import { addCreditCardSchema } from "../wallet/schemas";
import { Spacer } from "../../components/spacer/spacer";
import { ErrorMessage } from "../../components/error-message/error-message";
import moment from "moment";
import { Inline } from "../../components/layout/inline";
import { iconSize } from "../../theme/iconSize";
import { CreditCardFields, InfoBoxIconType, InfoBoxType } from "../../enums";
import { setDefaultPaymentSourceUpdated } from "../../redux/actions/actionCreators";
import { WebView } from "react-native-webview";
import { DomusoApiService } from "../../api/DomusoApiService";
import { customAlphabet } from "nanoid/non-secure";
import { RootState } from "../../redux/reducers";
import { testProps } from "../../utils/testProps";
import { ApiError } from "../../utils/error";
import { templateAddSupportLink } from "../../utils/languageTemplating";
const nanoid = customAlphabet("1234567890", 18);

const {
  CARD_HOLDER,
  CARD_NUMBER,
  CVV,
  STREET_ADDRESS,
  STREET_ADDRESS_2,
  CITY,
  STATE,
  EXP_DATE,
  ZIP,
} = CreditCardFields;

interface IProps {
  route: any;
}

const windowWidth = Dimensions.get("window").width;
let pagePaddingWidth = 0;

if (windowWidth > 1024) {
  pagePaddingWidth = windowWidth * 0.1;
}
if (windowWidth > 1260) {
  pagePaddingWidth = windowWidth * 0.3;
}

export const AddCreditCard = ({ route }: IProps) => {
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const theme = useTheme() as ThemeType;
  const { fontSize } = theme;
  const styles = getStyles(theme);
  const { colors, spacing } = theme;
  const [loading, setLoading] = useState(false);
  const [isContinueDisabled, setIsContinueDisabled] = useState(false);
  const { params = {} } = route;
  const { reservationId, isDomusoCash } = params;
  const cognitoUser = useSelector((state) => state.auth.cognitoUser);
  const [apiError, setApiError] = useState<any>(false);
  const [billingAddressError, setBillingAddressError] = useState<any>(false);
  const [date, setDateReact] = useState(new Date());
  const [tmId, setTmId] = useState(null);

  const {
    register,
    handleSubmit,
    setValue: originalSetValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(addCreditCardSchema),
  });
  const service = new DomusoApiService(cognitoUser, dispatch);
  const [shouldStoreCard, setShouldStoreCard] = useState(true);
  const storeCardValue = shouldStoreCard ? "checked" : "unchecked";
  const setValue = (formName: string, val: any) => {
    clearErrors(formName);
    setIsContinueDisabled(false);
    originalSetValue(formName, val);
  };
  const logger = useSelector((state: RootState) => state.logger);

  const inputAccessoryViewID = "inputAccessoryViewID4";
  const ref = [
    createRef(),
    createRef(),
    createRef(),
    createRef(),
    createRef(),
    createRef(),
    createRef(),
    createRef(),
    createRef(),
  ];
  const [currentInputIndex, setCurrentInputIndex] = useState(0);
  let webview: any = null;

  useEffect(() => {
    setTmId(`domus-${nanoid()}`);
    logger.info(`Set tmId to ${tmId}`);
  }, []);

  useEffect(() => {
    register(CARD_HOLDER);
    register(CARD_NUMBER);
    register(CVV);
    register(STREET_ADDRESS);
    register(STREET_ADDRESS_2);
    register(CITY);
    register(STATE);
    register(EXP_DATE);
    register(ZIP);
  }, [register]);

  // Should this be in redux?
  const onSubmit = async (data: any) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        reservationId,
        allowCof: !!shouldStoreCard,
        paymentSourceType: "CREDIT_CARD",
        cvv: data.cvv,
        threatMetrixSessionId: tmId,
      };

      if (isDomusoCash) {
        payload.paymentType = "DOMUSO_CASH";
      }

      const { payment_source_id: paymentSourceId } = await service.addPaymentSource(payload);
      dispatch(setDefaultPaymentSourceUpdated(paymentSourceId));
      setLoading(false);
      return navigation.goBack();
    } catch (apiError) {
      logger.error(apiError);
      const { code } = apiError;
      setIsContinueDisabled(true);
      setApiError(null);
      setBillingAddressError(null);
      if (
        [
          ERROR_CODES.CREDIT_CARD_INVALID_BILLING_ADDRESS_ERROR,
          ERROR_CODES.CREDIT_CARD_INVALID_BILLING_ZIP_ERROR,
        ].includes(code)
      ) {
        setBillingAddressError(translate("homeStack.avs.generic"));
        setLoading(false);
        return;
      } else if (code === ERROR_CODES.SCHEMA_VALIDATION_ERROR) {
        setApiError(apiError.message);
        setLoading(false);
        return;
      }
      const blockedRes = await service.checkUserCCPayBlock({ reservationId }).catch((e) => {
        logger.error(e);
        return { blocked: false };
      });
      if (blockedRes?.blocked) {
        setLoading(false);
        return navigation.goBack();
      }
      // Get error message to render from the apiError.
      let errorMessage = _getApiErrorMessageJSX(apiError);
      // Update state.
      setApiError(errorMessage);
    }
    setLoading(false);
  };

  /**
   *
   * @param apiError: Api response error from backend.
   * @return JSX component for API error banner to be rendered.
   */
  const _getApiErrorMessageJSX = (apiError: ApiError): JSX.Element => {
    let errorMessage: string = "";
    // Get message ID to be used for translation if applicable.
    const messageId = getErrorMessageId(apiError);
    if (messageId) {
      errorMessage = translate(messageId) || "";
    } else {
      // Check if no translation available and message present then show message.
      if (apiError.message) {
        // There is a chance that the error message does not have a code.
        errorMessage = apiError.message;
        // console.log("Error message fetched from backend");
      } else {
        errorMessage = translate("homeStack.unexpectedError");
        // console.log("Using the default error message");
      }
    }
    // Format message template.
    // Contact support link to add to error message if required.
    const errorWithSupportLink = templateAddSupportLink(errorMessage);
    const errorMessageJSX: JSX.Element = <Text>{errorWithSupportLink}</Text>;

    return errorMessageJSX;
  };

  const setCity = (formName: any, value: any) => {
    clearErrors(formName);
    setValue(formName, value);
  };
  const setUSState = (value: any) => {
    clearErrors(STATE);
    setValue(STATE, value);
  };

  const renderListItem = (text: string) => {
    const itemStyle = { paddingVertical: 0 };
    const bulletStyle = { fontSize: fontSize.largerPlus };
    const bulletItem = <Text style={bulletStyle}>{"•"}</Text>;
    return <List.Item title={apiError} style={itemStyle} left={() => bulletItem} />;
  };

  const renderErrorChild = () => {
    if (apiError && billingAddressError) {
      return (
        <>
          {renderListItem(apiError)}
          {renderListItem(billingAddressError)}
        </>
      );
    }
    let error;
    if (apiError) error = apiError;
    if (billingAddressError) error = billingAddressError;
    if (error) return <Text>{error}</Text>;
  };

  return (
    <>
      <View style={{ overflow: "hidden", width: "100%", height: 1 }}>
        <WebView
          ref={(ref) => (webview = ref)}
          originWhitelist={["*"]}
          useWebKit={true}
          scrollEnabled={false}
          style={{ marginTop: 0, marginBottom: 0, opacity: 0.99 }}
          startInLoadingState={true}
          javaScriptEnabled={true}
          source={{
            uri: `${service.url}/v1/threat_metrix_iframe?threatMetrixSessionId=${tmId}`,
            headers: {
              Authorization: "", // TODO: figure out how to get cognito token here
            },
          }}
        />
      </View>
      <Screen
        style={styles.container}
        preset="scroll"
        statusBar="dark-content"
        dontUseAutoPadding={true}
        keyboardOffset="keyboardNavigation"
        backgroundColor={theme.colors.background}>
        <Inline space="small" style={styles.ccContainer}>
          <Image
            accessible={true}
            accessibilityLabel={"MasterCard"}
            accessibilityRole={"image"}
            style={styles.ccImage}
            source={require("../../../assets/mastercard.png")}
          />
          <Image
            accessible={true}
            accessibilityLabel={"Discover"}
            accessibilityRole={"image"}
            style={styles.ccImage}
            source={require("../../../assets/discover.png")}
          />
          <Image
            accessible={true}
            accessibilityLabel={"Visa"}
            accessibilityRole={"image"}
            style={styles.ccImage}
            source={require("../../../assets/ccvisa.png")}
          />
        </Inline>
        <Stack space="none" horizontalSpace="medium">
          <Text tx="homeStack.creditCardDetails" style={styles.sectionTitle} />
          <LongRow
            styles={styles}
            errors={errors}
            keyboardType="number-pad"
            setValue={setValue}
            maxLength={19}
            render={(props: any) => <TextInputMask type={"credit-card"} {...props} />}
            formName={CARD_NUMBER}
            placeholder="1234 1234 1234 1234"
            inputAccessoryViewID={inputAccessoryViewID}
            ref={ref[0] as any}
            onFocus={() => setCurrentInputIndex(0)}
            onSubmitEditing={() => (ref[1].current as any).focus()}
          />
          <LongRow
            styles={styles}
            setValue={setValue}
            errors={errors}
            formName={CARD_HOLDER}
            placeholder="Name"
            inputAccessoryViewID={inputAccessoryViewID}
            ref={ref[1] as any}
            onFocus={() => setCurrentInputIndex(1)}
            onSubmitEditing={() => (ref[2].current as any).focus()}
            returnKeyType="next"
          />
          <CardInformationRow
            styles={styles}
            setValue={setValue}
            setError={setError}
            expDate={date}
            errors={errors}
            clearErrors={clearErrors}
            inputAccessoryViewID={inputAccessoryViewID}
            ref={[ref[2], ref[3]] as any}
            onFocus0={() => setCurrentInputIndex(2)}
            onSubmitEditing0={() => (ref[3].current as any).focus()}
            onFocus1={() => setCurrentInputIndex(3)}
            onSubmitEditing1={() => (ref[4].current as any).focus()}
          />
          <Text tx="homeStack.billingAddressHint" style={styles.sectionHint} />
          <Text
            tx="homeStack.billingAddress"
            style={{ ...styles.sectionTitle, marginTop: spacing.small }}
          />
          <LongRow
            styles={styles}
            errors={errors}
            setValue={setValue}
            formName={STREET_ADDRESS}
            inputAccessoryViewID={inputAccessoryViewID}
            ref={ref[4] as any}
            onFocus={() => setCurrentInputIndex(4)}
            onSubmitEditing={() => (ref[5].current as any).focus()}
            returnKeyType="next"
          />
          <LongRow
            styles={styles}
            errors={errors}
            setValue={setValue}
            formName={STREET_ADDRESS_2}
            inputAccessoryViewID={inputAccessoryViewID}
            ref={ref[5] as any}
            onFocus={() => setCurrentInputIndex(5)}
            onSubmitEditing={() => (ref[6].current as any).focus()}
            returnKeyType="next"
          />
          <CityStateRow
            styles={styles}
            setCity={setCity}
            setUSState={setUSState}
            setError={setError}
            errors={errors}
            inputAccessoryViewID={inputAccessoryViewID}
            ref={[ref[6], ref[7]] as any}
            onFocus0={() => setCurrentInputIndex(6)}
            // *Android only fix*
            // there is a case where ref[7].current does not have the focus function avaliable
            // so as a work around we will check to see if ref.current is null
            // the state/provice dropdown will not focus if it is not avaliable
            // not the best fix but will fix the crash in ENG-1721
            onSubmitEditing0={() => (ref[7].current ? (ref[7].current as any).focus() : null)}
            onFocus1={() => setCurrentInputIndex(7)}
            onSubmitEditing1={() => (ref[8].current as any).focus()}
          />
          <LongRow
            keyboardType="number-pad"
            errors={errors}
            styles={styles}
            setValue={setValue}
            formName={ZIP}
            inputAccessoryViewID={inputAccessoryViewID}
            ref={ref[8] as any}
            onFocus={() => setCurrentInputIndex(8)}
            onSubmitEditing={handleSubmit(onSubmit)}
            returnKeyType="go"
          />
          {Platform.OS === "ios" ? (
            <InputAccessoryNavigation
              inputAccessoryViewID={inputAccessoryViewID}
              ref={ref as any}
              currentInputIndex={currentInputIndex}
            />
          ) : null}
          <Spacer size="smaller" />
          <View accessible={true}>
            <Inline space="tiny">
              <Checkbox.Android
                color={colors.darkBg}
                status={storeCardValue}
                onPress={() => setShouldStoreCard(!shouldStoreCard)}
                {...testProps("store-card-option")}
              />
              <Text tx="homeStack.storeCard" />
            </Inline>
          </View>
          <Spacer size="larger" />
          <Stack style={{ paddingHorizontal: theme.spacing.small }}>
            {Boolean(apiError || billingAddressError) ? (
              <InfoBox
                width="100%"
                headline={translate("homeStack.error")}
                infoBoxType={InfoBoxType.RED}
                usesXClose={true}
                xCloseHandler={() => setApiError(null)}
                headlineIconType={InfoBoxIconType.RED_X}>
                {renderErrorChild()}
              </InfoBox>
            ) : null}
            <View style={styles.buttonContainer}>
              <Button
                loading={loading}
                disabled={isContinueDisabled}
                mode="contained"
                tx="walletStack.save"
                onPress={handleSubmit(onSubmit)}
                {...testProps("continue-button")}
              />
            </View>
          </Stack>
        </Stack>
      </Screen>
    </>
  );
};

interface LongRowProps {
  styles: any;
  errors?: Record<string, any>;
  setValue: any;
  formName: string;
  maxLength?: number;
  render?: any;
  keyboardType?: any;
  placeholder?: string;
  returnKeyType?:
    | "default"
    | "none"
    | "done"
    | "go"
    | "next"
    | "search"
    | "send"
    | "previous"
    | "google"
    | "join"
    | "route"
    | "yahoo"
    | "emergency-call"
    | undefined;
  inputAccessoryViewID?: string;
  onFocus?: any;
  onSubmitEditing?: any;
}

const LongRow = forwardRef(
  (
    {
      styles,
      setValue,
      formName,
      placeholder,
      errors,
      render,
      maxLength,
      keyboardType = "default",
      returnKeyType,
      inputAccessoryViewID,
      onFocus,
      onSubmitEditing,
    }: LongRowProps,
    ref: any
  ) => {
    const theme = useTheme() as ThemeType;
    return (
      <View accessible={true}>
        <Text tx={`walletStack.${formName}`} style={styles.label} />
        <TextInput
          error={errors?.[formName]?.message}
          mode="outlined"
          render={render}
          keyboardType={keyboardType}
          maxLength={maxLength}
          placeholder={placeholder}
          onChangeText={(text) => setValue(formName, text)}
          ref={ref}
          returnKeyType={returnKeyType}
          inputAccessoryViewID={inputAccessoryViewID}
          onFocus={onFocus}
          onSubmitEditing={onSubmitEditing}
          {...testProps(formName)}
        />
        <ErrorMessage errors={errors} formName={formName} />
      </View>
    );
  }
);

interface CityStateRowProps {
  setCity: Function;
  setUSState: Function;
  setError: Function;
  styles: any;
  errors: any;
  inputAccessoryViewID?: string;
  onFocus0?: any;
  onSubmitEditing0?: any;
  onFocus1?: any;
  onSubmitEditing1?: any;
}

const CityStateRow = forwardRef(
  (
    {
      setCity,
      setUSState,
      setError,
      styles,
      errors,
      inputAccessoryViewID,
      onFocus0,
      onSubmitEditing0,
      onFocus1,
      onSubmitEditing1,
    }: CityStateRowProps,
    ref: any
  ) => {
    const theme = useTheme() as ThemeType;
    return (
      <>
        <View accessible={true}>
          <Text tx="walletStack.city" style={styles.label} />
          <TextInput
            error={errors?.[CITY]?.message}
            mode="outlined"
            onChangeText={(text) => setCity(CITY, text)}
            ref={ref[0]}
            inputAccessoryViewID={inputAccessoryViewID}
            onFocus={onFocus0}
            onSubmitEditing={onSubmitEditing0}
            returnKeyType="next"
            {...testProps("city")}
          />
          <ErrorMessage errors={errors} formName={CITY} />
        </View>
        <View accessible={true}>
          <Text tx="walletStack.state" style={styles.label} />
          <USStateDropdown
            setValue={setUSState}
            formName={STATE}
            label={Platform.OS === "ios" ? undefined : "State"}
            setError={setError}
            errors={errors}
            ref={ref[1]}
            inputAccessoryViewID={inputAccessoryViewID}
            onFocus={onFocus1}
            onSubmitEditing={onSubmitEditing1}
          />
          <ErrorMessage errors={errors} formName={STATE} />
        </View>
      </>
    );
  }
);

interface CardInformationRowProps {
  setValue: Function;
  setError: Function;
  clearErrors: Function;
  expDate: Date;
  styles: any;
  errors: any;
  inputAccessoryViewID?: string;
  onFocus0?: any;
  onSubmitEditing0?: any;
  onFocus1?: any;
  onSubmitEditing1?: any;
}

const CardInformationRow = forwardRef(
  (
    {
      setValue,
      setError,
      styles,
      errors,
      clearErrors,
      inputAccessoryViewID,
      onFocus0,
      onSubmitEditing0,
      onFocus1,
      onSubmitEditing1,
    }: CardInformationRowProps,
    ref: any
  ) => {
    const setExpDate = (formName: string, value: any) => {
      clearErrors(formName);
      const now = moment().startOf("month");
      const selected = value.startOf("month");
      const formatted = value.format("MMYY");
      setValue(formName, formatted);
      if (selected < now) {
        setError(formName, { type: "invalid", message: "Invalid expiry date" });
      }
    };

    return (
      <View style={styles.cardInformationRow}>
        <View style={{ width: "48%" }}>
          <Text tx="walletStack.expirationDate" style={styles.label} />
          <View style={styles.expDate}>
            <MonthYearDropdown
              setValue={setExpDate}
              formName={EXP_DATE}
              setError={setError}
              label={Platform.OS === "ios" ? undefined : "Date"}
              errors={errors}
              ref={ref[0]}
              inputAccessoryViewID={inputAccessoryViewID}
              onFocus={onFocus0}
              onSubmitEditing={onSubmitEditing0}
            />
            <ErrorMessage errors={errors} formName={EXP_DATE} />
          </View>
        </View>
        <View style={{ width: "48%" }} accessible={true}>
          <Text tx="walletStack.cvv" style={styles.label} />
          <TextInput
            error={errors?.[CVV]?.message}
            keyboardType="number-pad"
            maxLength={3}
            mode="outlined"
            onChangeText={(text) => setValue(CVV, text)}
            ref={ref[1]}
            inputAccessoryViewID={inputAccessoryViewID}
            onFocus={onFocus1}
            onSubmitEditing={onSubmitEditing1}
            {...testProps("cvv")}
          />
          <ErrorMessage errors={errors} formName={CVV} />
        </View>
      </View>
    );
  }
);

const getStyles = (theme: ThemeType) => {
  const text = { color: theme.colors.surface };
  const { colors, spacing, fontSize } = theme;
  return StyleSheet.create({
    container: {
      ...Platform.select({
        native: {
          backgroundColor: colors.background,
          paddingTop: spacing.large,
          paddingBottom: spacing.large,
        },
        default: {
          maxHeight: 250,
          paddingLeft: pagePaddingWidth,
          paddingRight: pagePaddingWidth,
        },
      }),
    },
    ccContainer: { marginLeft: "auto", marginRight: "auto" },
    ccImage: {
      width: iconSize.larger,
      height: 45,
      borderRadius: 5,
    },
    sectionTitle: {
      fontSize: fontSize.large,
      fontWeight: "bold",
      marginTop: spacing.large,
      marginBottom: spacing.tiny,
    },
    label: {
      fontSize: fontSize.large,
      marginLeft: spacing.smaller,
      marginBottom: spacing.tiny,
      marginTop: spacing.small,
    },
    dateFields: {
      display: "flex",
      justifyContent: "space-between",
    },
    cardInformationRow: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    spaceBetween: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    },
    mediumRow: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
    },
    sectionHint: {
      fontSize: fontSize.large,
      marginTop: spacing.small,
      marginBottom: spacing.none,
      paddingTop: spacing.tiny,
      paddingBottom: spacing.none,
    },
    billingAddressError: {
      marginTop: spacing.small,
    },
    buttonContainer: {
      ...Platform.select({
        default: {
          width: 325,
          marginLeft: "auto",
          marginRight: "auto",
        },
      }),
    },
  });
};
